import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { billingApi } from '../services/api';
import { ACCOUNT_USAGE_QUERY_KEY, useAccountPlan, useAccountUsage } from '../hooks/useAccountUsage';
import {
  SUBSCRIPTION_SUMMARY_QUERY_KEY,
  useSubscriptionSummary,
} from '../hooks/useSubscriptionSummary';
import type { PlanCatalogPlan, SubscriptionSummary } from '../types/billing';
import { useFlag } from '../hooks/useFeatureFlags';
import { useBillingPortal } from '../hooks/useBillingPortal';

/**
 * SubscriptionPage — Settings → Billing → Subscription (task 628).
 *
 * The plan half of the retired Plan & Billing page, decluttered. It answers a
 * single question: *what am I subscribed to?* — plan identity, price, renewal
 * date, terms, included features, and the catalog for changing tiers. Live
 * usage numbers moved to the Billing page (task 630); this page keeps only a
 * one-line "this period so far" teaser that links there.
 *
 * Sources of truth (all live, never hardcoded plan data):
 *   - Current plan, caps, features: JWT plan claims (AuthContext).
 *   - Plan cards + feature display names: the AMS catalog (GET /api/billing/plans,
 *     task 232). `PLAN_TAGLINES` below is the ONLY client-side plan copy.
 *   - Renewal date + card summary: the subscription-summary read (task 627),
 *     rendered only when present — the endpoint is gracefully absent otherwise.
 *   - "This period so far" teaser: billed usage from GET /api/accounts/usage.
 *
 * Checkout only for the no-plan → paid case (Checkout cannot modify an existing
 * subscription). Tier changes between paid plans switch in the page (task 965);
 * the Change plan button on the current-plan card opens the Stripe Billing
 * Portal for cancellation and anything that switch refuses (task 1015, which
 * moved that door here off the Billing page).
 */

/** Convert basis-points markup (e.g. 12500) to a human multiplier (1.25). */
function markupMultiplier(bps: number | undefined): number | null {
  if (bps === undefined || bps < 0) return null;
  return bps / 10000;
}

function formatUsd(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

function formatCentsAsUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Whole-dollar price rendering for plan cards ($99, not $99.00). */
function formatCentsAsWholeUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : formatUsd(dollars);
}

/**
 * Marketing voice per plan slug — the ONLY plan copy that lives client-side.
 * Every number and capability on the cards comes from the fetched catalog;
 * unknown slugs simply render without a tagline.
 */
const PLAN_TAGLINES: Record<string, string> = {
  free: 'Included with every account',
  starter: 'Launch your first apps',
  growth: 'Embed beyond the platform',
  scale: 'Real-time, multi-step at scale',
  enterprise: 'Tailored limits and support',
};

/**
 * The Free plan (task 1021), recognised from the catalog rather than from a
 * hardcoded plan list: the `free` slug at $0 that AMS does NOT provision through
 * Stripe. `selfServe: false` and `basePriceCents: 0` is also Enterprise's shape,
 * which is why the slug is what separates the two — Free is a real plan every
 * account holds, Enterprise is a contact-sales tier.
 */
function isFreePlan(plan: PlanCatalogPlan): boolean {
  return plan.slug === 'free' && plan.basePriceCents === 0 && !plan.selfServe;
}

/** Derive the compare-card bullet list from plan data: limits first, then capabilities. */
function planCardBullets(plan: PlanCatalogPlan): string[] {
  const bullets: string[] = [];

  const maxEndpoints = plan.limits['max_endpoints'];
  if (maxEndpoints === -1) {
    bullets.push('Custom endpoint volume');
  } else if (maxEndpoints !== undefined) {
    bullets.push(`${maxEndpoints} endpoint${maxEndpoints === 1 ? '' : 's'} included`);
  }

  const extraPriceCents = plan.limits['extra_endpoint_price_cents'];
  if (extraPriceCents !== undefined && extraPriceCents > 0) {
    bullets.push(`${formatCentsAsWholeUsd(extraPriceCents)}/mo per extra endpoint`);
  }

  const markup = markupMultiplier(plan.limits['model_cost_markup_bps']);
  if (markup !== null && markup > 0) {
    bullets.push(`Usage billed at ${markup}× LLM cost`);
  }

  bullets.push(...plan.features.map((feature) => feature.name));
  return bullets;
}

/**
 * `SubscriptionSummary` lives in `../types/billing` (task 942) — it grew a
 * second face (the Stripe-billed plan alongside the AMS entitlement plan) and
 * more than one billing surface renders it. The query itself lives in
 * `../hooks/useSubscriptionSummary` (task 964), shared with the Billing page.
 */

/** Stripe's recurring interval as the short suffix the price line uses. */
function intervalSuffix(interval: string | null): string {
  if (interval === 'year') return 'yr';
  if (interval === 'month') return 'mo';
  return interval ?? 'mo';
}

function formatRenewalDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The service's machine-readable refusals of an in-page plan switch (task 965),
 * said in the customer's terms. Anything else is a generic failure — the raw
 * string is never shown.
 */
const SWITCH_REFUSALS: Record<string, string> = {
  no_billed_subscription:
    'This account has no Stripe subscription to change yet. Choose a plan through checkout, or contact support.',
  already_on_plan: 'Your subscription already bills this plan.',
  plan_not_available: 'That plan is not available right now. Please try again shortly.',
  plan_not_self_serve: 'That plan is not self-serve — please contact sales to move onto it.',
  owner_required: 'Only the account owner can change the plan.',
};

/**
 * How hard the page chases the webhook. AMS's plan pointer is written by
 * Stripe's `customer.subscription.updated`, which arrives shortly after the
 * change, so the summary is re-read a few times before the page settles.
 */
const SWITCH_POLL_ATTEMPTS = 5;
const SWITCH_POLL_INTERVAL_MS = 1500;

export default function SubscriptionPage() {
  const [redirecting, setRedirecting] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  // The Stripe Billing Portal door (task 1015). The checkout flow below keeps
  // its own state; the portal flow is shared with the Payment page, so it lives
  // in one hook rather than being written out a second time here.
  const {
    openPortal,
    redirecting: portalRedirecting,
    sessionError: portalError,
  } = useBillingPortal();
  // --- In-page plan switching (task 965) --------------------------------------
  // The plan card the User has asked to switch to, held while the confirmation
  // is open; the switch in flight; and the two outcomes.
  const [switchTarget, setSwitchTarget] = useState<PlanCatalogPlan | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [switchedTo, setSwitchedTo] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');
  // Plan + limits come from GET /api/accounts/usage (task 676 — previously the
  // JWT `plan` / `limits` claims). No plan slug = no active subscription
  // (there is no free tier).
  const { plan, limits, isPending: planPending } = useAccountPlan();
  const planSlug = plan?.toLowerCase() || null;
  const hasNoPlan = !planSlug;

  const includedEndpoints = limits['max_endpoints']; // -1 = unlimited
  const extraEndpointPriceCents = limits['extra_endpoint_price_cents'];
  const markup = markupMultiplier(limits['model_cost_markup_bps']);

  // --- Live plan catalog (task 232) -------------------------------------------
  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useQuery({
    queryKey: ['planCatalog'],
    queryFn: async () => {
      const res = await billingApi.getPlans();
      return res.data.data.plans;
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });

  // --- Current-period usage, for the "this period so far" teaser only ---------
  // The shared query — it also supplies the plan and limits above.
  const { data: usage } = useAccountUsage();

  // --- Subscription summary (task 627): renewal date + card, gracefully absent -
  // The shared query (task 964) — the Billing page renders the same read, so the
  // `['subscriptionSummary']` key has one definition rather than one per page.
  const { data: summary } = useSubscriptionSummary(!hasNoPlan);

  const currentPlan = catalog?.find((plan) => plan.slug === planSlug);
  const planLabel =
    currentPlan?.name ?? (planSlug ? titleCase(planSlug) : null);
  // Free is $0, not "Custom" (task 1021) — the custom-priced reading belongs to
  // the contact-sales tier alone.
  const currentPlanIsFree = currentPlan !== undefined && isFreePlan(currentPlan);
  const planPrice =
    currentPlan === undefined
      ? null
      : currentPlanIsFree
        ? '$0'
        : currentPlan.basePriceCents > 0
          ? `${formatCentsAsWholeUsd(currentPlan.basePriceCents)}/mo`
          : 'Custom';

  // --- Billing truth is Stripe (task 942) ------------------------------------
  // The headline plan and price on this card name what the customer is CHARGED,
  // so they come from the Stripe-billed plan whenever the summary read answers.
  // The AMS entitlement plan (which may deliberately differ — a comp, an
  // internal account) is shown separately and marked, never presented as the
  // billed plan. Without the Stripe read the catalog-derived labels stand, as
  // they did before.
  const billed = summary?.billedPlan ?? null;
  const entitlement = summary?.entitlementPlan ?? null;
  const isEntitlementOverride = entitlement?.isOverride === true;

  const billedLabel = billed?.name ?? (billed?.slug ? titleCase(billed.slug) : null);
  const billedPrice =
    billed?.amountCents != null && billed.amountCents > 0
      ? `${formatCentsAsWholeUsd(billed.amountCents)}/${intervalSuffix(billed.interval)}`
      : null;

  const displayPlanLabel = billedLabel ?? planLabel;
  const displayPlanPrice = billedPrice ?? planPrice;

  // The plan the compare grid calls "Current" is the plan Stripe BILLS (task
  // 965) — that is the one the switch changes. Only when the Stripe read is
  // absent does the entitlement plan stand in for it, as it did before.
  const billedSlug = billed?.slug ?? planSlug;
  const entitlementSlug = entitlement?.slug ?? planSlug;

  /**
   * The account holds a plan but Stripe bills nothing for it (task 1021).
   *
   * Two readings, both safe. The Free plan is never Stripe-provisioned, so its
   * slug alone settles it. Otherwise the summary read has to have ANSWERED and
   * reported no subscription: `undefined` is still in flight and `null` is an
   * unavailable read (not deployed, not an owner), and neither is evidence of
   * anything — treating either as "no subscription" would push a paying account
   * into Checkout. The Billing Portal has no session to open in this state; the
   * way onto a paid plan is Checkout.
   */
  const noStripeSubscription =
    currentPlanIsFree ||
    (summary != null && summary.status === null && summary.billedPlan === null);

  // --- Billed usage for the teaser (billed = raw × markup) --------------------
  const rawUsageDollars =
    usage?.rawCostMicroUsd !== undefined ? usage.rawCostMicroUsd / 1_000_000 : undefined;
  const billedUsageDollars =
    rawUsageDollars !== undefined ? rawUsageDollars * (markup ?? 1) : undefined;

  // --- "What's included" + upsell next tier -----------------------------------
  const currentFeatureNames = currentPlan?.features.map((f) => f.name) ?? [];
  const nextPlan = useMemo(() => {
    if (!catalog || currentPlan === undefined) return undefined;
    return catalog
      .filter((p) => p.displayOrder > currentPlan.displayOrder)
      .sort((a, b) => a.displayOrder - b.displayOrder)[0];
  }, [catalog, currentPlan]);
  const additionalInNextTier = useMemo(() => {
    if (!nextPlan || currentPlan === undefined) return 0;
    const currentKeys = new Set(currentPlan.features.map((f) => f.key));
    return nextPlan.features.filter((f) => !currentKeys.has(f.key)).length;
  }, [nextPlan, currentPlan]);

  /**
   * Start Stripe Checkout for a first plan. Checkout cannot modify an existing
   * subscription, so this is the no-plan → paid path only; paid-to-paid changes
   * switch in the page, and the portal door is `useBillingPortal` above (task
   * 1015, which took the portal branch out of here so the flow wiring exists
   * once).
   */
  const openCheckout = useCallback(async (planSlugArg?: string) => {
    setRedirecting(true);
    setSessionError(null);

    // Stripe returns to /settings/billing today (the historical return URL).
    const returnUrl = `${window.location.origin}/settings/billing?billing_return=1`;

    try {
      const response = await billingApi.createSession({
        mode: 'checkout',
        returnUrl,
        ...(planSlugArg ? { planSlug: planSlugArg } : {}),
      });
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 404 || e?.status === 501 || !e?.status) {
        setSessionError('Upgrade is not available yet. Please contact support to upgrade your plan.');
      } else {
        setSessionError('Something went wrong. Please try again.');
      }
      setRedirecting(false);
    }
  }, []);

  /**
   * Switch the BILLED plan without leaving the page (task 965).
   *
   * The service changes the price on the account's Stripe subscription and
   * Stripe prorates; AMS follows by webhook, so the plan the rest of the app
   * reads lags the answer by a beat. That is why this re-reads the summary a
   * few times until the billed plan catches up rather than trusting the first
   * read — and why the confirmation names the plan the RESPONSE reports, which
   * came straight from Stripe.
   */
  const confirmSwitch = useCallback(async () => {
    if (!switchTarget) return;
    const targetSlug = switchTarget.slug;

    setSwitching(true);
    setSwitchError(null);
    setSwitchedTo(null);

    try {
      const response = await billingApi.changePlan(targetSlug);
      const newBilled = response.data.data.billedPlan;
      setSwitchTarget(null);
      setSwitchedTo(newBilled?.name ?? switchTarget.name);

      // Usage limits follow the plan, so the teaser and the caps are re-read too.
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_USAGE_QUERY_KEY });

      // Poll the summary until the webhook-driven billed plan matches, then
      // stop. A handful of attempts: if Stripe's event is slower than that, the
      // page is still correct on its next ordinary read.
      for (let attempt = 0; attempt < SWITCH_POLL_ATTEMPTS; attempt += 1) {
        await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_SUMMARY_QUERY_KEY });
        const latest = queryClient.getQueryData<SubscriptionSummary | null>(
          SUBSCRIPTION_SUMMARY_QUERY_KEY,
        );
        if (latest?.billedPlan?.slug === targetSlug) break;
        await new Promise((resolve) => setTimeout(resolve, SWITCH_POLL_INTERVAL_MS));
      }
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string; response?: { error?: string } };
      const refusal = e?.response?.error ?? e?.message ?? '';
      setSwitchError(SWITCH_REFUSALS[refusal] ?? 'The plan change did not go through. Please try again.');
      setSwitchTarget(null);
    } finally {
      setSwitching(false);
    }
  }, [switchTarget, queryClient]);

  const scrollToCompare = () =>
    document
      .getElementById('subscription-compare-plans')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Plan not known yet — hold the render rather than flash the no-plan hero at
  // an account that has a plan (task 676).
  if (planPending) return null;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <PageHeader
        title="Subscription"
        description="Your plan, what it includes, and how to change tiers. Payment and invoices live on the Payment and Billing pages."
      />

      {/* Current plan — or the choose-a-plan hero for a no-plan account */}
      {hasNoPlan ? (
        <section
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          data-testid="subscription-no-plan-hero"
        >
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Current plan
          </h2>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-testid="subscription-current-plan">
            No plan yet
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="subscription-no-plan-note">
            Your account doesn't have a plan yet — choose a plan below to get started.
          </p>
          {canManageBilling ? (
            <button
              onClick={scrollToCompare}
              data-testid="subscription-choose-plan-button"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <ArrowUpCircleIcon className="h-4 w-4" />
              Choose a plan
            </button>
          ) : (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="subscription-member-note">
              Plan and billing are managed by your account owner.
            </p>
          )}
          {sessionError && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="subscription-session-error">
              {sessionError}
            </p>
          )}
        </section>
      ) : (
        <section
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          data-testid="subscription-current-plan-card"
        >
          <div className="sm:flex sm:items-start sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Current plan
              </h2>
              <div className="mt-1 flex items-baseline flex-wrap gap-x-3 gap-y-1">
                <span className="text-2xl font-semibold text-gray-900 dark:text-white" data-testid="subscription-current-plan">
                  {displayPlanLabel}
                </span>
                {displayPlanPrice && (
                  <span className="text-base text-gray-500 dark:text-gray-400" data-testid="subscription-current-price">
                    {displayPlanPrice}
                  </span>
                )}
                <span
                  className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-semibold"
                  data-testid="subscription-active-badge"
                >
                  Active
                </span>
              </div>
              {/* Entitlement override (task 942): AMS deliberately grants a plan
                  other than the one Stripe bills. Say so plainly rather than
                  letting the headline imply the richer plan is being charged.
                  The operator-facing override reason is NOT shown here. */}
              {isEntitlementOverride && entitlement && (
                <p
                  className="mt-2 text-sm text-gray-600 dark:text-gray-300"
                  data-testid="subscription-entitlement-override"
                >
                  Entitlements:{' '}
                  <span className="font-medium text-gray-800 dark:text-gray-100">{entitlement.name}</span>{' '}
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-xs font-semibold">
                    override
                  </span>
                </p>
              )}
              {/* Free (task 1021): there is no Stripe subscription behind this
                  plan, so say that in one line rather than leaving the renewal
                  and card slots blank or spinning. */}
              {currentPlanIsFree && (
                <p
                  className="mt-2 text-sm text-gray-500 dark:text-gray-400"
                  data-testid="subscription-free-summary"
                >
                  Free plan. No billing on file — a card is added when you choose a paid plan.
                </p>
              )}
              {/* Renewal + card summary — present only when the task-627 read answers */}
              {!currentPlanIsFree && summary && (summary.currentPeriodEnd || summary.paymentMethod) && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-testid="subscription-renewal-line">
                  {summary.currentPeriodEnd && (
                    <>
                      Renews{' '}
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {formatRenewalDate(summary.currentPeriodEnd)}
                      </span>
                    </>
                  )}
                  {summary.currentPeriodEnd && summary.paymentMethod && ' · '}
                  {summary.paymentMethod && (
                    <>
                      billed to {titleCase(summary.paymentMethod.brand)} ···· {summary.paymentMethod.last4}
                    </>
                  )}
                </p>
              )}
            </div>
            {/* Change plan is the Stripe Billing Portal door (task 1015). The
                compare grid below remains the in-page switch and the ordinary
                way to move between paid plans; this button is for cancellation
                and for anything that switch refuses. It sits on the page that
                owns the plan rather than on Billing, which answers what the
                account has been charged. */}
            {canManageBilling && !hasNoPlan && (
              noStripeSubscription ? (
                /* No Stripe subscription to manage (task 1021) — the portal
                   cannot open one, so the action is the compare grid below,
                   where each paid tier goes through Checkout. */
                <button
                  onClick={scrollToCompare}
                  data-testid="subscription-choose-paid-plan"
                  className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors sm:mt-0"
                >
                  <ArrowUpCircleIcon className="h-4 w-4" />
                  Choose a paid plan
                </button>
              ) : (
                <button
                  onClick={() => openPortal('plan')}
                  disabled={portalRedirecting}
                  data-testid="subscription-change-plan-button"
                  className="mt-4 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 text-sm font-medium rounded-md transition-colors sm:mt-0"
                >
                  {portalRedirecting ? 'Redirecting...' : 'Change plan'}
                </button>
              )
            )}
          </div>

          {!canManageBilling && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="subscription-member-note">
              Plan and billing are managed by your account owner.
            </p>
          )}

          {/* Outcome of an in-page switch (task 965). The success line names the
              plan Stripe reported back, and says the page is still catching up
              rather than implying every surface has already moved. */}
          {switchedTo && (
            <p
              className="mt-4 text-sm text-emerald-700 dark:text-emerald-300"
              data-testid="subscription-switch-success"
            >
              You're now billed for <span className="font-medium">{switchedTo}</span>. Stripe has
              prorated the change on your next invoice; this page updates as the change lands.
            </p>
          )}

          {switchError && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="subscription-switch-error">
              {switchError}
            </p>
          )}

          {(sessionError ?? portalError) && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="subscription-session-error">
              {sessionError ?? portalError}
            </p>
          )}

          {/* Plan-terms tiles */}
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Endpoints included</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="subscription-included-endpoints">
                {includedEndpoints === undefined
                  ? '—'
                  : includedEndpoints === -1
                    ? 'Unlimited'
                    : String(includedEndpoints)}
              </dd>
              {extraEndpointPriceCents !== undefined && extraEndpointPriceCents > 0 && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  then {formatCentsAsUsd(extraEndpointPriceCents)}/mo per extra endpoint
                </p>
              )}
            </div>
            <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <dt className="text-xs text-gray-500 dark:text-gray-400">Usage billing rate</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="subscription-usage-rate">
                {markup === null ? '—' : `${markup.toFixed(2)}× LLM cost`}
              </dd>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">metered from the first dollar</p>
            </div>
            <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <dt className="text-xs text-gray-500 dark:text-gray-400">This period so far</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white" data-testid="subscription-period-so-far">
                {billedUsageDollars !== undefined ? formatUsd(billedUsageDollars) : '—'}
              </dd>
              <a
                href="/settings/billing"
                data-testid="subscription-billing-teaser-link"
                className="mt-0.5 inline-block text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                See breakdown → Billing
              </a>
            </div>
          </dl>
        </section>
      )}

      {/* What's included — the current plan's feature list, two columns + upsell */}
      {!hasNoPlan && currentFeatureNames.length > 0 && (
        <section
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          data-testid="subscription-whats-included"
        >
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">What's included</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Everything your {planLabel} plan unlocks.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            {currentFeatureNames.map((name) => (
              <div key={name} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <CheckIcon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                {name}
              </div>
            ))}
            {nextPlan && additionalInNextTier > 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400" data-testid="subscription-upsell-line">
                ＋ {additionalInNextTier} more in {nextPlan.name} — compare below
              </div>
            )}
          </div>
        </section>
      )}

      {/* Compare plans — rendered from the live AMS catalog, never hardcoded */}
      <section id="subscription-compare-plans">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
          Compare plans
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Endpoints and usage are billed on top of every plan. Changing between paid plans is prorated by Stripe.
        </p>

        {catalogLoading && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="subscription-plan-cards-loading"
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 animate-pulse"
              >
                <div className="h-5 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="mt-3 h-8 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="mt-4 space-y-2">
                  <div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-700/60" />
                  <div className="h-3 w-5/6 rounded bg-gray-100 dark:bg-gray-700/60" />
                  <div className="h-3 w-4/6 rounded bg-gray-100 dark:bg-gray-700/60" />
                </div>
              </div>
            ))}
          </div>
        )}

        {catalogError && !catalogLoading && (
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="subscription-catalog-error">
            Plans are unavailable right now. Your current plan above is unaffected — please try again shortly.
          </p>
        )}

        {catalog && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {catalog.map((plan) => {
              // "Current" is the plan Stripe BILLS (task 965) — the one the
              // switch below changes. On an overridden account the granted plan
              // is a different card, and says so with its own chip rather than
              // competing for this one.
              const isCurrent = billedSlug === plan.slug;
              const isEntitledByOverride =
                isEntitlementOverride && entitlementSlug === plan.slug && !isCurrent;
              // Free is $0, not custom priced (task 1021) — only the
              // contact-sales tier keeps the Custom price and the sales line.
              const isFree = isFreePlan(plan);
              const isCustomPriced = !plan.selfServe && plan.basePriceCents === 0 && !isFree;
              // Checkout renders for every account with no Stripe subscription
              // to modify — no plan at all, or the Free plan (task 1021):
              // Stripe Checkout cannot modify an existing subscription, and
              // there is none here to modify.
              const showAction =
                canManageBilling &&
                plan.selfServe &&
                !isCurrent &&
                (hasNoPlan || noStripeSubscription);
              // A Stripe-subscribed account switches in place instead (task
              // 965): the service repoints its subscription at this plan's price.
              const showSwitch =
                canManageBilling &&
                plan.selfServe &&
                !isCurrent &&
                !hasNoPlan &&
                !noStripeSubscription;
              return (
                <div
                  key={plan.slug}
                  data-testid={`subscription-plan-card-${plan.slug}`}
                  className={`flex flex-col rounded-lg border p-5 bg-white dark:bg-gray-800 ${
                    isCurrent
                      ? 'border-indigo-500 ring-1 ring-indigo-500'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                    {isCurrent && (
                      <span
                        data-testid={isFree ? 'plan-card-free-current' : undefined}
                        className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
                      >
                        Current
                      </span>
                    )}
                    {isEntitledByOverride && (
                      <span
                        data-testid={`subscription-plan-card-entitled-chip-${plan.slug}`}
                        className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-xs font-semibold"
                      >
                        Entitled (override)
                      </span>
                    )}
                  </div>
                  <p className="mt-1">
                    <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {isCustomPriced ? 'Custom' : formatCentsAsWholeUsd(plan.basePriceCents)}
                    </span>
                    {!isCustomPriced && !isFree && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
                    )}
                  </p>
                  {PLAN_TAGLINES[plan.slug] && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{PLAN_TAGLINES[plan.slug]}</p>
                  )}
                  <ul className="mt-4 space-y-2 flex-1">
                    {planCardBullets(plan).map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <CheckIcon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  {showAction && (
                    <button
                      onClick={() => openCheckout(plan.slug)}
                      disabled={redirecting}
                      data-testid={`subscription-plan-card-upgrade-button-${plan.slug}`}
                      className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      {redirecting ? 'Redirecting...' : 'Choose plan'}
                    </button>
                  )}
                  {showSwitch && (
                    <button
                      onClick={() => {
                        setSwitchError(null);
                        setSwitchedTo(null);
                        setSwitchTarget(plan);
                      }}
                      disabled={switching}
                      data-testid={`subscription-plan-card-switch-button-${plan.slug}`}
                      className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-60 text-sm font-medium rounded-md transition-colors"
                    >
                      Switch to this plan
                    </button>
                  )}
                  {/* Contact sales belongs to the tier that is genuinely
                      custom priced. Free is not self-serve either, but it needs
                      no action at all — every account already has it (task 1021). */}
                  {!plan.selfServe && !isCurrent && !isFree && (
                    <p className="mt-4 text-center text-sm font-medium text-indigo-600 dark:text-indigo-400">
                      Contact sales
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Moving onto a paid plan — from Free or from no plan at all — opens Stripe Checkout.
          Switching between paid plans happens here and
          is prorated by Stripe. Cancellation, and any change this page can't make, open the Stripe
          Billing Portal from the Change plan button above — Innovate never sees your payment details.
        </p>
      </section>

      {/* Confirming a switch (task 965). It names the BILLED plan on both sides,
          the price, the proration and the date the new amount is first charged —
          and, on an overridden account, that the entitlements AMS grants are not
          what is changing here. */}
      <ConfirmDialog
        isOpen={switchTarget !== null}
        onClose={() => setSwitchTarget(null)}
        onConfirm={confirmSwitch}
        title="Switch your plan"
        confirmLabel="Switch plan"
        confirmStyle="primary"
        isLoading={switching}
        message={
          switchTarget ? (
            <div className="space-y-3" data-testid="subscription-switch-confirm">
              <p>
                Your subscription moves from{' '}
                <span className="font-medium">{displayPlanLabel ?? 'your current plan'}</span>
                {displayPlanPrice ? ` (${displayPlanPrice})` : ''} to{' '}
                <span className="font-medium">{switchTarget.name}</span> at{' '}
                <span className="font-medium">
                  {formatCentsAsWholeUsd(switchTarget.basePriceCents)}/mo
                </span>
                .
              </p>
              <p>
                Stripe prorates the change: you are charged or credited for the rest of this period
                {billed?.nextBillingDate
                  ? `, and the new amount is billed in full from ${formatRenewalDate(billed.nextBillingDate)}`
                  : ''}
                .
              </p>
              {isEntitlementOverride && entitlement && (
                <p data-testid="subscription-switch-override-note">
                  Your account's entitlements are{' '}
                  <span className="font-medium">{entitlement.name}</span> by override and do not
                  change with the billed plan.
                </p>
              )}
            </div>
          ) : (
            ''
          )
        }
      />
    </div>
  );
}
